import axios from 'axios';

// Use relative URL if BACKEND_URL is not set or if we're on a production domain
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Check if we're on a custom domain (not preview or localhost)
const isProductionDomain = typeof window !== 'undefined' && 
  window.location.hostname !== 'localhost' && 
  !window.location.hostname.includes('preview.emergentagent.com');

// Get the correct API base URL
export const getApiBaseUrl = () => {
  if (isProductionDomain) {
    return window.location.origin;
  }
  return BACKEND_URL;
};

const API_BASE = isProductionDomain ? '/api' : `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('gardenbook_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('gardenbook_user');
      localStorage.removeItem('gardenbook_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  registerAdmin: (data) => api.post('/auth/register-admin', data),
  requestPasswordReset: (email, userType) => api.post('/auth/request-password-reset', { email, user_type: userType }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Admin APIs
export const adminApi = {
  getLicenses: () => api.get('/admin/licenses'),
  createLicense: (data) => api.post('/admin/licenses', data),
  updateLicense: (id, isActive) => api.put(`/admin/licenses/${id}`, null, { params: { is_active: isActive } }),
  getLicenseDetail: (id) => api.get(`/admin/licenses/${id}`),
  patchLicense: (id, data) => api.patch(`/admin/licenses/${id}`, data),
  recordPayment: (id, data) => api.post(`/admin/licenses/${id}/payments`, data),
  getCompanies: () => api.get('/admin/companies'),
  getCompanyDetail: (id) => api.get(`/admin/companies/${id}`),
  suspendCompany: (id, suspended) => api.put(`/admin/companies/${id}/suspend`, null, { params: { suspended } }),
  getStats: () => api.get('/admin/stats'),
  
  // Promo Codes
  getPromoCodes: () => api.get('/admin/promo-codes'),
  createPromoCode: (data) => api.post('/admin/promo-codes', data),
  updatePromoCode: (id, isActive) => api.put(`/admin/promo-codes/${id}`, null, { params: { is_active: isActive } }),
  deletePromoCode: (id) => api.delete(`/admin/promo-codes/${id}`),
  validatePromoCode: (code) => api.post('/admin/promo-codes/validate', null, { params: { code } }),
  
  // Company Suspension
  suspendCompany: (companyId, suspended) => api.put(`/admin/companies/${companyId}/suspend`, null, { params: { suspended } }),
  
  // App Settings / Branding
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  
  // Configuration (Stripe, SMTP, Trial)
  getConfig: () => api.get('/admin/config'),
  updateConfig: (data) => api.put('/admin/config', data),
  testSmtp: () => api.post('/admin/config/test-smtp'),
  
  // Tickets (Admin)
  getTickets: () => api.get('/admin/tickets'),
  updateTicket: (id, data) => api.put(`/admin/tickets/${id}`, data),
  
  // Changelog (Admin)
  getChangelog: () => api.get('/changelog'),
  createChangelog: (data) => api.post('/admin/changelog', data),
  deleteChangelog: (id) => api.delete(`/admin/changelog/${id}`),
  
  // Invoices (Admin)
  getInvoices: () => api.get('/admin/invoices'),
  getInvoice: (id) => api.get(`/admin/invoices/${id}`),
  createInvoice: (data) => api.post('/admin/invoices', data),
  markInvoicePaid: (id, paymentMethod = 'manual') => api.put(`/admin/invoices/${id}/mark-paid`, null, { params: { payment_method: paymentMethod } }),
  deleteInvoice: (id) => api.delete(`/admin/invoices/${id}`),
  getInvoicePdf: (id) => api.get(`/admin/invoices/${id}/pdf`),
  sendInvoice: (id) => api.post(`/admin/invoices/${id}/send`),
  
  // Company management (Admin)
  updateCompany: (id, data) => api.put(`/admin/companies/${id}`, data),
  deleteCompany: (id) => api.delete(`/admin/companies/${id}`),
  sendCompanyPasswordReset: (id) => api.post(`/admin/companies/${id}/send-password-reset`),
  changeCompanyPassword: (id, newPassword) => api.put(`/admin/companies/${id}/change-password`, { new_password: newPassword }),
  impersonateCompany: (id) => api.post(`/admin/impersonate/company/${id}`),
  impersonateClient: (id) => api.post(`/admin/impersonate/client/${id}`),
  endImpersonation: (sessionId) => api.post('/admin/impersonation/end', { session_id: sessionId }),
  getImpersonationAudit: (params = {}) => api.get('/admin/impersonation-audit', { params }),
  getImpersonationSession: (sessionId) => api.get(`/admin/impersonation-audit/${sessionId}`),
  
  // License deletion
  deleteLicense: (id) => api.delete(`/admin/licenses/${id}`),
  
  // Announcements (Admin)
  getAnnouncements: () => api.get('/admin/announcements'),
  createAnnouncement: (data) => api.post('/admin/announcements', data),
  updateAnnouncement: (id, data) => api.put(`/admin/announcements/${id}`, data),
  deleteAnnouncement: (id) => api.delete(`/admin/announcements/${id}`),
  
  // Roadmap Items (Admin internal tracking)
  getRoadmapItems: () => api.get('/admin/roadmap'),
  createRoadmapItem: (data) => api.post('/admin/roadmap', data),
  updateRoadmapItem: (id, data) => api.put(`/admin/roadmap/${id}`, data),
  deleteRoadmapItem: (id) => api.delete(`/admin/roadmap/${id}`),
  
  // Push Notifications
  getVapidKey: () => api.get('/push/vapid-key'),
  subscribeToPush: (subscription) => api.post('/push/subscribe', subscription),
  unsubscribeFromPush: () => api.delete('/push/unsubscribe'),
  getPushStatus: () => api.get('/push/status'),
};

// Public APIs
export const publicApi = {
  getChangelog: () => api.get('/changelog'),
  getAppVersion: () => api.get('/app/version'),
  getPricing: () => api.get('/pricing/public'),
};

// Public Settings API
export const settingsApi = {
  getPublic: () => api.get('/settings/public'),
};

// Notifications API
export const notificationsApi = {
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markBatchRead: (ids) => api.put('/notifications/batch-read', { ids }),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
  deleteAll: () => api.delete('/notifications'),
};

// Company APIs
export const companyApi = {
  register: (data) => api.post('/company/register', data),
  getProfile: () => api.get('/company/profile'),
  updateProfile: (data) => api.put('/company/profile', data),

  // Widget booking-mode (validation manuelle / auto pour tous les widgets)
  getWidgetsBookingMode: () => api.get('/company/widgets/booking-mode'),
  setWidgetsBookingMode: (mode) => api.put('/company/widgets/booking-mode', { booking_mode: mode }),
  getLicense: () => api.get('/company/license'),
  getInvoices: () => api.get('/company/invoices'),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/company/upload-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getStats: () => api.get('/company/stats'),
  getMRR: () => api.get('/company/mrr'),
  updateHourlyRate: (rate) => api.put('/company/hourly-rate', { default_hourly_rate: rate }),
  
  // Account management (email/password change)
  changeEmail: (data) => api.put('/company/change-email', data),
  changePassword: (data) => api.put('/company/change-password', data),
  
  // Closures (fermetures exceptionnelles)
  getClosures: () => api.get('/company/closures'),
  createClosure: (data) => api.post('/company/closures', data),
  deleteClosure: (id) => api.delete(`/company/closures/${id}`),
  
  // Slot Types (types de créneaux personnalisés)
  getSlotTypes: () => api.get('/company/slot-types'),
  createSlotType: (data) => api.post('/company/slot-types', data),
  updateSlotType: (id, data) => api.put(`/company/slot-types/${id}`, data),
  deleteSlotType: (id) => api.delete(`/company/slot-types/${id}`),
  
  // Collaborators
  getCollaborators: () => api.get('/company/collaborators'),
  createCollaborator: (data) => api.post('/company/collaborators', data),
  updateCollaborator: (id, data) => api.put(`/company/collaborators/${id}`, data),
  deleteCollaborator: (id) => api.delete(`/company/collaborators/${id}`),
  sendCollaboratorPasswordReset: (id) => api.post(`/company/collaborators/${id}/send-password-reset`),
  getCollaboratorWorkHours: (id) => api.get(`/company/collaborators/${id}/work-hours`),
  updateCollaboratorWorkHours: (id, data) => api.put(`/company/collaborators/${id}/work-hours`, data),

  // Trash / test-flag universel
  softDeleteEntity: (entity, id) => api.post(`/company/trash/${entity}/${id}/soft-delete`),
  restoreEntity: (entity, id) => api.post(`/company/trash/${entity}/${id}/restore`),
  setTestFlag: (entity, id, isTest) => api.put(`/company/trash/${entity}/${id}/test-flag`, { is_test: isTest }),
  getTrash: () => api.get('/company/trash'),
  uploadCollaboratorAvatar: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/company/collaborators/${id}/upload-avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Collaborator Leaves (Congés)
  getCollaboratorLeaves: (collabId) => api.get(`/company/collaborators/${collabId}/leaves`),
  getAllLeaves: (status) => api.get('/company/leaves', { params: status ? { status } : {} }),
  createCollaboratorLeave: (collabId, data) => api.post(`/company/collaborators/${collabId}/leaves`, data),
  deleteLeave: (leaveId) => api.delete(`/company/leaves/${leaveId}`),
  
  // Leave Requests (Demandes de congés)
  getLeaveRequests: () => api.get('/company/leave-requests'),
  approveLeaveRequest: (requestId, comment) => api.post(`/company/leave-requests/${requestId}/approve`, null, { params: comment ? { comment } : {} }),
  rejectLeaveRequest: (requestId, comment) => api.post(`/company/leave-requests/${requestId}/reject`, null, { params: comment ? { comment } : {} }),
  
  // Clients (CRM)
  getClients: () => api.get('/company/clients'),
  getClient: (id) => api.get(`/company/clients/${id}`),
  updateClient: (id, data) => api.put(`/company/clients/${id}`, data),
  createClient: (data) => api.post('/company/clients/create', data),
  addCredits: (clientId, data) => api.post(`/company/clients/${clientId}/add-credits`, data),
  activateSubscription: (clientId) => api.post(`/company/clients/${clientId}/activate-subscription`),
  // Client account approval
  getPendingClients: () => api.get('/company/clients/pending'),
  approveClient: (clientId) => api.post(`/company/clients/${clientId}/approve`),
  rejectClient: (clientId, reason) => api.post(`/company/clients/${clientId}/reject`, null, { params: { reason } }),
  
  // Client CSV Import/Export
  exportClientsCSV: () => api.get('/company/clients/export-csv', { responseType: 'blob' }),
  getCSVTemplate: () => api.get('/company/clients/csv-template', { responseType: 'blob' }),
  importClientsCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/company/clients/import-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Client Exchanges (Suivi des échanges)
  getClientExchanges: (clientId) => api.get(`/company/clients/${clientId}/exchanges`),
  createClientExchange: (clientId, data) => api.post(`/company/clients/${clientId}/exchanges`, data),
  deleteClientExchange: (clientId, exchangeId) => api.delete(`/company/clients/${clientId}/exchanges/${exchangeId}`),
  
  // Send credentials email to client
  sendClientCredentials: (clientId) => api.post(`/company/clients/${clientId}/send-credentials`),
  sendClientActivationLink: (clientId) => api.post(`/company/clients/${clientId}/send-activation-link`),
  sendClientPasswordReset: (clientId) => api.post(`/company/clients/${clientId}/send-password-reset`),
  
  // SMTP Test
  testSmtpConnection: () => api.post('/company/smtp/test-connection'),
  sendTestEmail: (recipient) => api.post('/company/smtp/send-test', recipient ? { recipient } : {}),
  getSmtpTestHistory: () => api.get('/company/smtp/test-history'),

  // Email history (sent via send_branded_email — auto-purged after 15 days)
  getEmailLogs: (params = {}) => api.get('/company/email-logs', { params }),
  getEmailLog: (logId) => api.get(`/company/email-logs/${logId}`),
  clearEmailLogs: () => api.delete('/company/email-logs'),

  // Health status (SMTP + Widgets)
  getHealthStatus: () => api.get('/company/health-status'),
  runHealthChecksNow: () => api.post('/company/health-status/run'),
  
  // Bookings for clients
  createBookingForClient: (data) => api.post('/company/bookings/for-client', data),
  createAppointmentForClient: (data) => api.post('/company/bookings/for-client/appointment', data),
  
  // Interventions
  getCompletedInterventions: () => api.get('/company/interventions/completed'),
  uploadInvoice: (interventionId, data) => api.put(`/company/interventions/${interventionId}/invoice`, data),
  uploadInvoiceFile: (interventionId, file, amount) => {
    const formData = new FormData();
    formData.append('file', file);
    if (amount) formData.append('invoice_amount', amount);
    return api.post(`/company/interventions/${interventionId}/upload-invoice`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteInvoice: (interventionId) => api.delete(`/company/interventions/${interventionId}/invoice`),
  // Intervention photos
  uploadInterventionPhoto: (interventionId, file, photoType = 'after', caption = null, shareWithClient = true) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/company/interventions/${interventionId}/upload-photo`, formData, {
      params: { photo_type: photoType, caption, share_with_client: shareWithClient },
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteInterventionPhoto: (interventionId, photoId) => api.delete(`/company/interventions/${interventionId}/photos/${photoId}`),
  updateInterventionPhoto: (interventionId, photoId, data) => api.put(`/company/interventions/${interventionId}/photos/${photoId}`, null, { params: data }),
  validateInterventionByCompany: (interventionId, data) => api.post(`/company/interventions/${interventionId}/validate-by-company`, data),
  
  // Reviews
  getReviews: () => api.get('/company/reviews'),
  getReviewStats: () => api.get('/company/review-stats'),
  
  // Subscription Plans
  getSubscriptionPlans: () => api.get('/subscription/plans'),
  
  // Services
  getServices: () => api.get('/company/services'),
  createService: (data) => api.post('/company/services', data),
  updateService: (id, data) => api.put(`/company/services/${id}`, data),
  deleteService: (id) => api.delete(`/company/services/${id}`),
  uploadServiceImage: (serviceId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/company/services/${serviceId}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Service Categories
  getServiceCategories: () => api.get('/company/service-categories'),
  createServiceCategory: (data) => api.post('/company/service-categories', data),
  updateServiceCategory: (id, data) => api.put(`/company/service-categories/${id}`, data),
  deleteServiceCategory: (id) => api.delete(`/company/service-categories/${id}`),
  
  // Questions
  getQuestions: () => api.get('/company/questions'),
  createQuestion: (data) => api.post('/company/questions', data),
  deleteQuestion: (id) => api.delete(`/company/questions/${id}`),
  
  // Subscription Requests
  getSubscriptionRequests: () => api.get('/company/subscription-requests'),
  getAllRequests: () => api.get('/company/all-requests'),
  archiveRequest: (id, isArchived = true) => api.put(`/company/requests/${id}/archive`, { is_archived: isArchived }),
  getRdvPlanning: (weekStart) => api.get(`/company/rdv-planning?week_start=${weekStart}`),
  assignRequestCollaborator: (id, payload) => {
    // Backwards compat: accept either a raw string (single collab) or an
    // object with `collaborator_id` / `collaborator_ids` for multi-assign.
    const body = typeof payload === 'string' ? { collaborator_id: payload } : payload;
    return api.put(`/company/requests/${id}/assign-collaborator`, body);
  },
  // Duplicate a booking to another date (planning quick-clone)
  duplicateBooking: (id, data) => api.post(`/company/bookings/${id}/duplicate`, data),
  getSuggestedCollaborators: (id) => api.get(`/company/requests/${id}/suggested-collaborators`),
  approveSubscription: (id) => api.post(`/company/subscription-requests/${id}/approve`),
  rejectSubscription: (id, reason) => api.post(`/company/subscription-requests/${id}/reject`, null, { params: { reason } }),
  updateSubscriptionRequest: (id, data) => api.put(`/company/subscription-requests/${id}`, data),
  proposeDatesToClient: (requestId, data) => api.post(`/company/subscription-requests/${requestId}/propose-dates`, data),
  getSubscriptionRequestBookings: (requestId) => api.get(`/company/subscription-requests/${requestId}/bookings`),
  getSubscriptionStats: (subscriptionId) => api.get(`/company/subscriptions/${subscriptionId}/stats`),
  addBookingToSubscription: (subscriptionId, date, slotType, slotPeriod) => 
    api.post(`/company/subscriptions/${subscriptionId}/add-booking`, null, { params: { date, slot_type: slotType, slot_period: slotPeriod } }),
  rescheduleBooking: (bookingId, newDate, newPeriod) => 
    api.put(`/company/bookings/${bookingId}/reschedule`, null, { params: { new_date: newDate, new_period: newPeriod } }),
  
  // Availability
  getAvailability: () => api.get('/company/availability'),
  createAvailability: (data) => api.post('/company/availability', data),
  deleteAvailability: (id) => api.delete(`/company/availability/${id}`),
  
  // Closures (Fermetures / Congés)
  getClosures: () => api.get('/company/closures'),
  createClosure: (data) => api.post('/company/closures', data),
  deleteClosure: (id) => api.delete(`/company/closures/${id}`),
  
  // Bookings & Clients
  getBookings: () => api.get('/company/bookings'),
  validateBooking: (bookingId, data = {}) => api.post(`/company/bookings/${bookingId}/validate`, data),
  rejectBooking: (bookingId, reason = '') => api.post(`/company/bookings/${bookingId}/reject`, null, { params: { reason } }),
  assignCollaboratorToBooking: (bookingId, collaboratorId) => api.put(`/company/bookings/${bookingId}/assign`, null, { params: { collaborator_id: collaboratorId } }),
  updateBookingStatus: (bookingId, status, bookingType = 'subscription') => 
    api.put(`/company/bookings/${bookingId}/status`, null, { params: { status, booking_type: bookingType } }),
  getClients: () => api.get('/company/clients'),
  createClient: (data) => api.post('/company/clients/create', data),
  getSubscriptions: () => api.get('/company/subscriptions'),
  
  // Widgets
  getWidgets: () => api.get('/company/widgets'),
  createWidget: (data) => api.post('/company/widgets', data),
  updateWidget: (id, data) => api.put(`/company/widgets/${id}`, data),
  toggleWidgetActive: (id, isActive) => api.patch(`/company/widgets/${id}/active`, { is_active: isActive }),
  autoFixTaxCredit: () => api.post('/company/services/auto-fix-tax-credit'),
  
  deleteWidget: (id) => api.delete(`/company/widgets/${id}`),
  addDispatcherDemo: () => api.post('/company/widgets/add-dispatcher-demo'),
  
  // Punctual Bookings
  getPunctualBookings: () => api.get('/company/punctual-bookings'),
  confirmPunctualBooking: (id) => api.post(`/company/punctual-bookings/${id}/confirm`),
  cancelPunctualBooking: (id) => api.post(`/company/punctual-bookings/${id}/cancel`),
  updatePunctualBooking: (id, data) => api.put(`/company/punctual-bookings/${id}`, data),
  
  // Appointment Requests (RDV)
  getAppointmentRequests: () => api.get('/company/appointment-requests'),
  approveAppointment: (id) => api.post(`/company/appointment-requests/${id}/approve`),
  rejectAppointment: (id, reason) => api.post(`/company/appointment-requests/${id}/reject`, null, { params: { reason } }),
  
  // Service Families (Familles de services)
  getServiceFamilies: () => api.get('/company/service-families'),
  createServiceFamily: (data) => api.post('/company/service-families', data),
  updateServiceFamily: (id, data) => api.put(`/company/service-families/${id}`, data),
  deleteServiceFamily: (id) => api.delete(`/company/service-families/${id}`),
  uploadServiceFamilyImage: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/company/service-families/${id}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Work Hours (Horaires de travail - prestations classiques)
  getWorkHours: () => api.get('/company/work-hours'),
  createWorkHoursSlot: (data) => api.post('/company/work-hours', data),
  updateWorkHoursSlot: (id, data) => api.put(`/company/work-hours/${id}`, data),
  deleteWorkHoursSlot: (id) => api.delete(`/company/work-hours/${id}`),
  bulkUpdateWorkHours: (slots) => api.put('/company/work-hours/bulk-update', { slots }),
  
  // Commercial Slots (Créneaux du commercial)
  getCommercialSlots: () => api.get('/company/commercial-slots'),
  createCommercialSlot: (data) => api.post('/company/commercial-slots', data),
  deleteCommercialSlot: (id) => api.delete(`/company/commercial-slots/${id}`),
  
  // Commercial Unavailabilities (Indisponibilités)
  getCommercialUnavailabilities: () => api.get('/company/commercial-unavailabilities'),
  createCommercialUnavailability: (data) => api.post('/company/commercial-unavailabilities', data),
  deleteCommercialUnavailability: (id) => api.delete(`/company/commercial-unavailabilities/${id}`),
  
  // Quote Requests (Demandes de devis)
  getQuoteRequests: () => api.get('/company/quote-requests'),
  getQuoteRequest: (id) => api.get(`/company/quote-requests/${id}`),
  updateQuoteRequestStatus: (id, status, rdvReport, quoteAmount) => 
    api.put(`/company/quote-requests/${id}/status`, null, { 
      params: { status, rdv_report: rdvReport, quote_amount: quoteAmount } 
    }),
  updateQuoteRequestReport: (id, rdvReport) => 
    api.put(`/company/quote-requests/${id}/report`, null, { params: { rdv_report: rdvReport } }),
  
  // Commercial Status & Followups
  updateCommercialStatus: (requestId, data) => api.put(`/company/requests/${requestId}/commercial-status`, data),
  createFollowup: (requestId, data) => api.post(`/company/requests/${requestId}/followups`, data),
  getRequestFollowups: (requestId) => api.get(`/company/requests/${requestId}/followups`),
  
  // Update Requests (modification des demandes)
  updateSubscriptionRequest: (id, data) => api.put(`/company/subscription-requests/${id}`, data),
  updateBooking: (id, data) => api.put(`/company/bookings/${id}`, data),
  updateAppointment: (id, data) => api.put(`/company/appointments/${id}`, data),
  updateAppointmentMeetingLink: (id, data) => api.put(`/company/appointments/${id}/meeting-link`, data),
  
  // Client Stats
  getClientStats: (clientId) => api.get(`/company/clients/${clientId}/stats`),
  getClientQuoteRequests: (clientId) => api.get(`/company/clients/${clientId}/quote-requests`),
  
  // Payments Dashboard (Tableau de bord des paiements)
  getPayments: (params = {}) => api.get('/company/payments', { params }),
  getPaymentStats: () => api.get('/company/payments/stats'),
  getPaymentDetails: (id) => api.get(`/company/payments/${id}`),
  
  // Invoices (Factures clients - B2C)
  getClientInvoices: (params = {}) => api.get('/company/client-invoices', { params }),
  downloadClientInvoice: (id) => `${getApiBaseUrl()}/api/company/client-invoices/${id}/download`,
  
  // Cancel subscription booking
  cancelBooking: (id) => api.delete(`/company/bookings/${id}`),
  
  // Email Templates
  getEmailTemplates: () => api.get('/company/email-templates'),
  saveEmailTemplate: (data) => api.post('/company/email-templates', data),
  deleteEmailTemplate: (id) => api.delete(`/company/email-templates/${id}`),
  
  // Subscription & Stripe
  getSubscriptionPlans: () => api.get('/subscription/plans'),
  getCurrentSubscription: () => api.get('/subscription/current'),
  createCheckoutSession: (planId) => api.post('/subscription/create-checkout', { 
    plan_id: planId, 
    origin_url: window.location.origin 
  }),
  getSubscriptionStatus: (sessionId) => api.get(`/subscription/status/${sessionId}`),
};

// Client APIs
export const clientApi = {
  getProfile: () => api.get('/client/profile'),
  updateProfile: (data) => api.put('/client/profile', data),
  // Address book
  listAddresses: () => api.get('/client/addresses'),
  addAddress: (data) => api.post('/client/addresses', data),
  updateAddress: (id, data) => api.put(`/client/addresses/${id}`, data),
  deleteAddress: (id) => api.delete(`/client/addresses/${id}`),
  uploadAvatar: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/client/upload-avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getSubscriptions: () => api.get('/client/subscriptions'),
  getBookings: () => api.get('/client/bookings'),
  getAvailability: () => api.get('/client/availability'),
  createBooking: (data) => api.post('/client/bookings', data),
  cancelBooking: (id) => api.delete(`/client/bookings/${id}`),
  getInvoices: () => api.get('/client/invoices'),
  getCompletedInterventions: () => api.get('/client/interventions/completed'),
  // Intervention validation
  getPendingValidations: () => api.get('/client/interventions/pending-validation'),
  validateIntervention: (interventionId) => api.post(`/client/interventions/${interventionId}/validate`),
  disputeIntervention: (interventionId, reason) => api.post(`/client/interventions/${interventionId}/dispute`, null, { params: { reason } }),
  // Reviews
  getPendingReviews: () => api.get('/client/pending-reviews'),
  submitReview: (data) => api.post('/client/reviews', data),
  getReviews: () => api.get('/client/reviews'),
  // Tickets / Bug reports
  getTickets: () => api.get('/client/tickets'),
  createTicket: (data) => api.post('/client/tickets', data),
  // Subscription scheduling (flexible planning)
  getSubscriptionSchedulingInfo: (subscriptionId) => api.get(`/client/subscriptions/${subscriptionId}/scheduling-info`),
  scheduleSubscriptionSlot: (subscriptionId, data) => api.post(`/client/subscriptions/${subscriptionId}/schedule-slot`, data),
  // NEW: Subscription detail with stats
  getSubscriptionStats: (subscriptionId) => api.get(`/client/subscriptions/${subscriptionId}/stats`),
  rescheduleBooking: (bookingId, newDate, newPeriod) => 
    api.put(`/client/bookings/${bookingId}/reschedule`, null, { params: { new_date: newDate, new_period: newPeriod } }),
  // Widgets exposed in the client space
  getAvailableWidgets: () => api.get('/client/available-widgets'),
};

// Collaborator APIs
export const collaboratorApi = {
  getProfile: () => api.get('/collaborator/profile'),
  getBookings: () => api.get('/collaborator/bookings'),
  getTodayBookings: () => api.get('/collaborator/today'),
  getStats: () => api.get('/collaborator/stats'),
  getMyAppointments: () => api.get('/collaborator/my-appointments'),
  decideAppointment: (id, decision, reason) => api.put(`/collaborator/appointments/${id}/decision`, { decision, reason }),
  updateBookingStatus: (id, status, bookingType = 'subscription') => 
    api.put(`/collaborator/bookings/${id}/status`, null, { params: { status, booking_type: bookingType } }),
  uploadPhotos: (id, data, bookingType = 'subscription') => 
    api.post(`/collaborator/bookings/${id}/photos`, data, { params: { booking_type: bookingType } }),
  uploadInterventionPhoto: (interventionId, file, photoType = 'after', caption = null, shareWithClient = true) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/collaborator/interventions/${interventionId}/upload-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { photo_type: photoType, caption, share_with_client: shareWithClient }
    });
  },
  
  // Leave requests (Demandes de congés)
  getMyLeaves: () => api.get('/collaborator/my-leaves'),
  requestLeave: (data) => api.post('/collaborator/request-leave', data),
  cancelLeaveRequest: (leaveId) => api.delete(`/collaborator/leaves/${leaveId}`),
};

// Widget APIs (public)
export const widgetApi = {
  getConfig: (companyId) => api.get(`/widget/${companyId}/config`),
  getEstimate: (companyId, passages, slotType, pricePerHour = 35) => 
    api.post(`/widget/${companyId}/estimate`, null, { params: { passages, slot_type: slotType, price_per_hour: pricePerHour } }),
  subscribe: (companyId, data) => api.post(`/widget/${companyId}/subscribe`, data),
  
  // Dispatcher widget (choice screen)
  getDispatcherConfig: (widgetId) => api.get(`/widget/dispatcher-widget/${widgetId}/config`),
  
  // Recurring widget (subscription)
  getRecurringConfig: (widgetId) => api.get(`/widget/recurring/${widgetId}/config`),
  getSubscriptionConfig: (widgetId) => api.get(`/widget/subscription/${widgetId}/config`),
  
  // Punctual widget
  getPunctualConfig: (widgetId) => api.get(`/widget/punctual/${widgetId}/config`),
  getPunctualAvailability: (widgetId, date, serviceId) => 
    api.get(`/widget/punctual/${widgetId}/availability`, { params: { date, service_id: serviceId } }),
  getPunctualCollaborators: (widgetId, serviceId, opts = {}) =>
    api.get(`/widget/punctual/${widgetId}/collaborators`, {
      params: { service_id: serviceId, date: opts.date, start_time: opts.start_time, end_time: opts.end_time, slot_period: opts.slot_period },
    }),
  createPunctualBooking: (widgetId, data) => api.post(`/widget/punctual/${widgetId}/book`, data),
  createRecurringBooking: (widgetId, data) => api.post(`/widget/punctual/${widgetId}/book/recurring`, data),
  
  // Appointment widget (RDV)
  getAppointmentConfig: (widgetId) => api.get(`/widget/appointment/${widgetId}/config`),
  getAppointmentAvailability: (widgetId, date, serviceId) => 
    api.get(`/widget/appointment/${widgetId}/availability`, { params: { date, service_id: serviceId } }),
  getAppointmentWeekAvailability: (widgetId, startDate) => 
    api.get(`/widget/appointment/${widgetId}/week-availability`, { params: { start_date: startDate } }),
  createAppointmentBooking: (widgetId, data) => api.post(`/widget/appointment/${widgetId}/book`, data),
  
  // Commercial availability for quote requests
  getCommercialAvailability: (widgetId, date) => 
    api.get(`/widget/punctual/${widgetId}/commercial-availability`, { params: { date } }),
  createQuoteRequest: (widgetId, data) => api.post(`/widget/punctual/${widgetId}/quote-request`, data),
  
  // Work hours availability for classic services
  getWorkAvailability: (widgetId, date = null) => 
    api.get(`/widget/punctual/${widgetId}/work-availability`, { params: date ? { date } : {} }),
  
  // Client payments
  createPayment: (widgetId, data) => api.post(`/widget/${widgetId}/create-payment`, data),
  getPaymentStatus: (sessionId) => api.get(`/widget/payment/status/${sessionId}`),
  
  // Announcements (public)
  getActiveAnnouncements: () => api.get('/announcements/active'),
};

export default api;
